import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import { useSelector } from "react-redux";
import helpContent from "./helpContent";

// ── Context for sharing open/close state ──
const HelpPanelContext = createContext({
  open: false,
  toggle: () => {},
  hasContent: false,
});

export const useHelpPanel = () => useContext(HelpPanelContext);

// ── Provider wraps the project layout ──
export const HelpPanelProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { leaf } = useSelector((state) => state.nav);
  const hasContent = !!helpContent[leaf];

  // Close when screen changes
  useEffect(() => {
    setOpen(false);
  }, [leaf]);

  const toggle = useCallback(() => setOpen((o) => !o), []);

  return (
    <HelpPanelContext.Provider value={{ open, toggle, setOpen, hasContent }}>
      {children}
      <HelpDrawer />
    </HelpPanelContext.Provider>
  );
};

// ── Slide-out drawer ──
const HelpDrawer = () => {
  const { open, setOpen } = useContext(HelpPanelContext);
  const [visible, setVisible] = useState(false);
  const [activeSection, setActiveSection] = useState("steps");
  const drawerRef = useRef(null);
  const { leaf } = useSelector((state) => state.nav);
  const content = helpContent[leaf];

  // Reset section when screen changes
  useEffect(() => {
    setActiveSection("steps");
  }, [leaf]);

  // Animate open/close
  useEffect(() => {
    if (open) {
      setVisible(true);
    } else {
      const timer = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, setOpen]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, setOpen]);

  if (!content) return null;

  const hasTips = content.tips && content.tips.length > 0;

  return (
    <>
      {/* Backdrop */}
      {visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.3)",
            zIndex: 998,
            opacity: open ? 1 : 0,
            transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            pointerEvents: open ? "auto" : "none",
          }}
        />
      )}

      {/* Drawer */}
      {visible && (
        <div
          ref={drawerRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: "380px",
            maxWidth: "90vw",
            backgroundColor: "var(--ev-white)",
            zIndex: 999,
            transform: open ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            flexDirection: "column",
            boxShadow: "4px 0 30px rgba(0,0,0,0.15)",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "1.25rem 1.5rem",
              background: "linear-gradient(135deg, var(--ev-purple), #79589F)",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <i className="fa-duotone fa-circle-question" style={{ fontSize: "1.1rem", opacity: 0.9 }} />
                <span style={{ fontWeight: 800, fontSize: "1.6rem", letterSpacing: "0.01em" }}>
                  {content.title}
                </span>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  width: "28px",
                  height: "28px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.8rem",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <p style={{
              fontSize: "0.95rem",
              marginTop: "0.6rem",
              opacity: 0.9,
              lineHeight: 1.5,
              fontWeight: 500,
            }}>
              {content.intro}
            </p>
          </div>

          {/* Tab bar */}
          {hasTips && (
            <div style={{
              display: "flex",
              borderBottom: "1px solid var(--ev-border)",
              flexShrink: 0,
            }}>
              {[
                { key: "steps", label: "Steps", icon: "fa-duotone fa-list-ol" },
                { key: "tips", label: "Tips", icon: "fa-duotone fa-lightbulb" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveSection(tab.key)}
                  style={{
                    flex: 1,
                    padding: "0.65rem 0",
                    background: "none",
                    border: "none",
                    borderBottom: activeSection === tab.key
                      ? "2px solid var(--ev-purple)"
                      : "2px solid transparent",
                    color: activeSection === tab.key
                      ? "var(--ev-purple)"
                      : "var(--ev-text-muted)",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.35rem",
                    transition: "all 0.2s ease",
                  }}
                >
                  <i className={tab.icon} style={{ fontSize: "0.75rem" }} />
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Content area */}
          <div style={{
            flex: 1,
            overflowY: "auto",
            padding: "1.25rem 1.5rem",
          }}>
            {activeSection === "steps" && (
              <div>
                {content.steps.map((step, i) => (
                  <StepItem key={`${leaf}-${i}`} index={i} text={step} />
                ))}
              </div>
            )}

            {activeSection === "tips" && hasTips && (
              <div>
                {content.tips.map((tip, i) => (
                  <TipItem key={`${leaf}-tip-${i}`} text={tip} delay={i * 60} />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: "0.75rem 1.5rem",
            borderTop: "1px solid var(--ev-border)",
            flexShrink: 0,
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              fontSize: "0.7rem",
              color: "var(--ev-text-muted)",
            }}>
              <i className="fa-duotone fa-keyboard" style={{ fontSize: "0.65rem" }} />
              <span>Press <kbd style={{
                padding: "0.1em 0.4em",
                borderRadius: "4px",
                background: "var(--ev-surface)",
                border: "1px solid var(--ev-border)",
                fontSize: "0.65rem",
                fontFamily: "monospace",
              }}>Esc</kbd> to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Step item with staggered fade-in ──
const StepItem = ({ index, text }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), index * 80);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div
      style={{
        display: "flex",
        gap: "0.75rem",
        marginBottom: "1rem",
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(8px)",
        transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          width: "26px",
          height: "26px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, var(--ev-purple), #79589F)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontWeight: 800,
          flexShrink: 0,
          marginTop: "1px",
        }}
      >
        {index + 1}
      </div>
      <p style={{
        fontSize: "0.95rem",
        lineHeight: 1.6,
        color: "var(--ev-text)",
        margin: 0,
      }}>
        {text}
      </p>
    </div>
  );
};

// ── Tip item with staggered fade-in ──
const TipItem = ({ text, delay }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      style={{
        display: "flex",
        gap: "0.65rem",
        marginBottom: "0.85rem",
        padding: "0.75rem",
        borderRadius: "8px",
        backgroundColor: "var(--ev-surface)",
        border: "1px solid var(--ev-border)",
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(8px)",
        transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <i
        className="fa-duotone fa-lightbulb"
        style={{
          color: "#E8A735",
          fontSize: "0.85rem",
          flexShrink: 0,
          marginTop: "2px",
        }}
      />
      <p style={{
        fontSize: "0.92rem",
        lineHeight: 1.55,
        color: "var(--ev-text)",
        margin: 0,
      }}>
        {text}
      </p>
    </div>
  );
};

export default HelpPanelProvider;
